import timezoneService from '@/services/timezone.service';
import uuidService from '@/services/uuid.service';
import { getZonedDateTimeFromLocaleString } from '@/timetime/timetime';
import { ICalendar } from '@/types/ICalendar';
import { IInvitation } from '@/types/calendar-event/IAttendee';
import { ICalendarEvent } from '@/types/calendar-event/ICalendarEvent';
import { IInterval } from '@/types/common/IInterval';
import { t } from 'i18next';
import { useFieldArray, useForm } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';

interface IArgs {
	event: ICalendarEvent;
	calendar: ICalendar;
	onClose: () => void;
	onDelete: (event: ICalendarEvent) => void;
	onSave: (event: ICalendarEvent) => void;
}

type ICalendarEventWithCommentsObject = Omit<ICalendarEvent, 'comments'> & {
	comments: Array<{ value: string }>;
};

export function useEventDetailsFormController(args: IArgs) {
	const form = useForm<ICalendarEventWithCommentsObject>({
		defaultValues: {
			...args.event,
			comments: args.event.comments.map((value) => ({ value })) ?? [],
		},
	});

	const formParticipant = useForm({
		defaultValues: {
			email: '',
			displayTimeMode: 'EXACT' as IInvitation['displayTimeMode'],
			summary: t('You have been invited to an event'),
			subtitle: t('Please accept or decline the invitation.'),
			description: '',
		},
	});

	const { remove, append, fields } = useFieldArray({
		control: form.control,
		name: 'attendees',
	});

	const commentsField = useFieldArray({
		control: form.control,
		name: 'comments',
	});

	const handleFormParticipantSubmit = formParticipant.handleSubmit((values) => {
		append({
			email: values.email,
			id: uuidService.v4(),
			status: 'NEEDS_ACTION',
			invitation: {
				id: uuidService.v4(),
				displayTimeMode: values.displayTimeMode,
				summary: values.summary,
				subtitle: values.subtitle,
				description: values.description,
			},
		});
		formParticipant.reset();
	});

	function handleRemoveParticipantClick(index: number) {
		remove(index);
	}

	function handleDeleteEventClick() {
		const event = { ...form.watch(), comments: form.watch().comments.map((o) => o.value) };
		return args.onDelete(event);
		// return removeCalendarEventMutation.mutateAsync({ id: args.event.id }).then(args.onClose);
	}

	function handleSave() {
		const values = form.watch();

		if (
			_hasNewInterval(values.interval, args.event.interval) &&
			args.calendar.tags.TT_STATUS_WHEN_DATES_CHANGED === 'SET_TENTATIVE'
		) {
			values.status = 'TENTATIVE';
		}

		// Transform the date input into a valid value for the backend
		if (values.interval?.inclusiveStart?.dateTime) {
			values.interval.inclusiveStart = _localDateTimeToISODateTime(
				values.interval.inclusiveStart.dateTime,
				timezoneService.getTimeZone()
			);
		}

		// Transform the date input into a valid value for the backend
		if (values.interval?.exclusiveEnd?.dateTime) {
			values.interval.exclusiveEnd = _localDateTimeToISODateTime(
				values.interval.exclusiveEnd.dateTime,
				timezoneService.getTimeZone()
			);
		}

		args.onSave({ ...values, calendarId: args.calendar.id, comments: values.comments.map((o) => o.value) });
		args.onClose();
	}

	function handleDeleteDateClick() {
		form.setValue('interval', null);
	}

	return {
		remove,
		handleFormParticipantSubmit,
		handleRemoveParticipantClick,
		handleDeleteEventClick,
		handleSave,
		handleDeleteDateClick,
		commentsField,
		fields,
		form,
		formParticipant,
		// Transform this into values suitable for the datetime-local
		from: _isoDateTimeToLocalDateTime(
			form.watch('interval')?.inclusiveStart?.dateTime ?? '',
			form.watch('interval')?.inclusiveStart?.timeZone ?? ''
		),
		to: _isoDateTimeToLocalDateTime(
			form.watch('interval')?.exclusiveEnd?.dateTime ?? '',
			form.watch('interval')?.exclusiveEnd?.timeZone ?? ''
		),
	};
}

/**
 * Transform an ISO date with timezone and offset to just a local dateTime.
 */
function _isoDateTimeToLocalDateTime(zonedDateTimeStr: string, timeZone: string | null): string {
	if (!zonedDateTimeStr) {
		return '';
	}

	timeZone = timeZone || timezoneService.getTimeZone();
	const zonedDateTime = Temporal.ZonedDateTime.from(`${zonedDateTimeStr}[${timeZone}]`);

	return zonedDateTime.toPlainDateTime().toString({ smallestUnit: 'minute' });
}

function _localDateTimeToISODateTime(localDateTime: string, timeZone: string): { dateTime: string; timeZone: string } {
	return {
		dateTime: getZonedDateTimeFromLocaleString(localDateTime, timeZone).toString().split('[')[0],
		timeZone,
	};
}

/**
 * export interface IInterval {
	inclusiveStart: {
		dateTime: string; // ISO 8601 format date-time
		timeZone: string | null; // Time zone of the start dateTime
	};
	exclusiveEnd: {
		dateTime: string; // ISO 8601 format date-time
		timeZone: string | null// Time zone of the end dateTime
	};
}
 */
function _hasNewInterval(intervalA: IInterval | null, intervalB: IInterval | null): boolean {
	if (intervalA === null && intervalB === null) {
		return false;
	}

	if (intervalA === null && intervalB !== null) {
		return true;
	}

	if (intervalA !== null && intervalB === null) {
		return true;
	}

	const intervalAStart = Temporal.PlainDateTime.from(intervalA!.inclusiveStart.dateTime);
	const intervalAEnd = Temporal.PlainDateTime.from(intervalA!.exclusiveEnd.dateTime);

	const intervalBStart = Temporal.PlainDateTime.from(intervalB!.inclusiveStart.dateTime);
	const intervalBEnd = Temporal.PlainDateTime.from(intervalB!.exclusiveEnd.dateTime);

	return !intervalAStart.equals(intervalBStart) || !intervalAEnd.equals(intervalBEnd);
}
